import {
  Component,
  ContentChild,
  contentChildren,
  EventEmitter,
  HostListener,
  inject,
  input,
  OnInit,
  output,
  Output,
  signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  from,
  map,
  Observable,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { LocalStorageSubject, LocalStorageUtils } from '@/utils/local-storage-subject';
import { DataColumnDirective } from './data-column.directive';
import { NbComponentStatus, NbDialogService, NbIconConfig } from '@nebular/theme';
import { tryWithLoading } from '@/utils/async-utils';
import { DataTablePdfService, SortDto } from '@artemis-software/wr-api';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DataTableFilterDirective } from '@shared/generic/data-table/data-table-filter.directive';
import { ExportFormatDialogComponent } from '@components/dialog/export-format-dialog/export-format-dialog.component';
import { isUnknownSortPropertyError } from '@/utils/sort-error';

interface BaseFilter {
  sort?: SortDto;
  page?: number;
  size?: number;
}

export interface CustomAction {
  icon?: NbIconConfig;
  title?: string;
  status: NbComponentStatus;
  tooltip?: string;
  action: () => unknown;
}

@Component({
  standalone: false,
  selector: 'wr-new-data-table[loadItemsFunction][getCountFunction][localStorageIdentifier]',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
})
export class DataTableComponent<Entity, Filter extends BaseFilter> implements OnInit {

  private readonly formBuilder = inject(FormBuilder);
  private readonly dataTablePdfService = inject(DataTablePdfService);
  private readonly dialogService = inject(NbDialogService);

  @ContentChild(DataTableFilterDirective)
  filterForm!: DataTableFilterDirective;

  readonly form = input<FormGroup>(this.formBuilder.group({}));
  readonly localStorageIdentifier = input.required<string>();

  readonly headerText = input<string>();
  readonly infoBanner = input<string | undefined>();
  readonly showNewButton = input<boolean>(true);
  readonly isAdmin = input<boolean>(false);
  readonly defaultPageSize= input<number>(10);
  readonly defaultSort = input<SortDto | undefined>(undefined);
  readonly deprecatedRows = input<boolean>(false);
  readonly insideCard = input<boolean>(true);
  readonly loadItemsFunction = input.required<(filter: Filter) => Observable<Entity[]>>();
  readonly getCountFunction = input.required<(filter: Filter) => Observable<number>>();
  readonly customActions = input<CustomAction[]>([]);
  readonly externalLoading$ = input<BehaviorSubject<boolean>>();
  readonly filterOverride = input<Partial<Filter> | undefined>(undefined);
  readonly filterApplied = output<Filter>();

  private static corruptedKeysRemoved = false;
  readonly pageSizes = [5, 10, 20, 50, 100];
  readonly ceil = (n: number): number => Math.ceil(n);

  readonly columns = contentChildren(DataColumnDirective);
  private readonly settledColumns$ = toObservable(this.columns).pipe(debounceTime(0));

  @Output()
  lineClick = new EventEmitter<Entity>();
  @Output()
  newClick = new EventEmitter();
  @Output()
  reload = new EventEmitter();

  data$?: Observable<{ count: number; items: Entity[] }>;


  private static removeCorruptedStorageKeys(): void {
    if (DataTableComponent.corruptedKeysRemoved) {
      return;
    }
    DataTableComponent.corruptedKeysRemoved = true;
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith('dok-') && /-(sorting|page|page-size)$/.test(key) && /[(){}\s]/.test(key)) {
        localStorage.removeItem(key);
      }
    }
  }

  pageSize$!: LocalStorageSubject<number>;
  readonly page$ = new BehaviorSubject(0);
  sorting$!: LocalStorageSubject<SortDto | undefined>;
  private rejectedSorts$!: BehaviorSubject<string[]>;
  private readonly appliedSort = signal<SortDto | undefined>(undefined);
  filter$ = new BehaviorSubject<Filter>({} as Filter);
  loading$ = new BehaviorSubject(false);
  exportLoading$ = new BehaviorSubject(false);
  refresh$ = new BehaviorSubject<void>(undefined);

  ngOnInit(): void {
    DataTableComponent.removeCorruptedStorageKeys();
    const identifier = this.localStorageIdentifier();
    const prefix = identifier ? `${identifier}-` : '';
    this.pageSize$ = new LocalStorageSubject(prefix + 'page-size', this.defaultPageSize());
    this.sorting$ = new LocalStorageSubject<SortDto | undefined>(prefix + 'sorting', this.defaultSort());
    this.rejectedSorts$ = new BehaviorSubject(this.storedRejectedSorts());
    this.dropRejectedSort();
    this.recoverFilter();
    this.filter$.subscribe(() => {
      this.persistFilter();
    });

    const applicableSort$ = combineLatest([this.sorting$, this.rejectedSorts$, this.settledColumns$]).pipe(
      map(([sorting, rejectedSorts]) => this.applicableSort(sorting, rejectedSorts)),
      distinctUntilChanged(),
      tap((sort) => this.appliedSort.set(sort)),
    );

    this.data$ = combineLatest([
      this.pageSize$,
      this.page$,
      applicableSort$,
      this.filter$,
      this.refresh$,
    ]).pipe(
      debounceTime(100),
      tap(() => this.loading$.next(true)),
      map(([pageSize, page, sorting, filter]) => {
        return {
          sort: sorting,
          page,
          size: pageSize,
          ...filter,
        };
      }),
      switchMap((requestFilter) =>
        from(
          tryWithLoading(this.loading$, async () => {
            try {
              const items = await firstValueFrom(this.loadItemsFunction()(requestFilter));
              const count = await firstValueFrom(this.getCountFunction()(requestFilter));
              return { items, count };
            } catch (error) {
              // handled inside the promise, not in a catchError: switchMap may already have torn the
              // inner subscription down (user typed in the filter) and the recovery would be lost
              return this.handleLoadFailure(error, requestFilter.sort?.sort);
            }
          }),
        ),
      ),
      // undefined means "keep the previous rows" instead of flashing an empty table
      filter((result): result is { count: number; items: Entity[] } => result !== undefined),
      shareReplay(1),
    );

    this.filter$.subscribe(() => this.page$.next(0));
  }

  private applicableSort(sorting: SortDto | undefined, rejectedSorts: string[]): SortDto | undefined {
    const key = sorting?.sort;
    if (key && (key === this.defaultSort()?.sort || this.isSortableColumnKey(key))) {
      return sorting;
    }
    return this.fallbackSort(rejectedSorts);
  }

  private fallbackSort(rejectedSorts: string[]): SortDto | undefined {
    const defaultSort = this.defaultSort();
    if (!defaultSort?.sort || rejectedSorts.includes(defaultSort.sort)) {
      return undefined;
    }
    return defaultSort;
  }

  private isSortableColumnKey(key: string): boolean {
    return this.columns().some((column) => column.sortable && column.key === key);
  }

  sortOrderOf(column: DataColumnDirective): SortDto.OrderEnum | undefined {
    const sorting = this.appliedSort();
    if (!sorting || !column.sortable || sorting.sort !== column.key) {
      return undefined;
    }
    return sorting.order;
  }

  private handleLoadFailure(error: unknown, sentSortKey: string | undefined): { items: Entity[]; count: number } | undefined {
    console.error('Data table load failed:', error);
    if (isUnknownSortPropertyError(error) && sentSortKey) {
      // remembered per table: without this a rejected defaultSort is re-seeded on every mount and
      // costs another 400 plus toast on every single page visit
      this.rememberRejectedSort(sentSortKey);
      if (this.sorting$.value?.sort === sentSortKey) {
        this.sorting$.next(undefined);
      }
      return undefined;
    }
    return { items: [], count: 0 };
  }

  private get rejectedSortsKey(): string {
    return `${this.localStorageIdentifier()}-rejected-sorts`;
  }

  private dropRejectedSort(): void {
    const current = this.sorting$.value?.sort;
    if (current && this.rejectedSorts$.value.includes(current)) {
      this.sorting$.next(undefined);
    }
  }

  private storedRejectedSorts(): string[] {
    const stored = LocalStorageUtils.get<string[]>(this.rejectedSortsKey);
    return Array.isArray(stored) ? stored : [];
  }

  private rememberRejectedSort(key: string): void {
    const rejected = this.storedRejectedSorts();
    if (!rejected.includes(key)) {
      this.storeRejectedSorts([...rejected, key]);
    }
  }

  private forgetRejectedSort(key: string): void {
    const rejected = this.storedRejectedSorts();
    if (rejected.includes(key)) {
      this.storeRejectedSorts(rejected.filter((entry) => entry !== key));
    }
  }

  private storeRejectedSorts(rejected: string[]): void {
    LocalStorageUtils.set(this.rejectedSortsKey, rejected);
    this.rejectedSorts$.next(rejected);
  }

  toggleSort(column: DataColumnDirective): void {
    if (!column.sortable) {
      return;
    }

    const current = this.appliedSort();
    // an explicit click must be able to retry a key the server rejected before it was fixed
    this.forgetRejectedSort(column.key);
    this.sorting$.next({
      sort: column.key,
      order:
        current?.order === SortDto.OrderEnum.Asc && current?.sort === column.key
          ? SortDto.OrderEnum.Desc
          : SortDto.OrderEnum.Asc,
    });
  }

  changePageSize(pageSize: number) {
    this.pageSize$.next(pageSize);
    this.page$.next(0);
  }

  public applyFormValueToFilter(): void {
    const filter = this.form().value;
    this.filter$.next(filter);
    this.filterApplied.emit(filter);
  }

  recoverFilter(): void {
    this.migrateLegacyFilterKey();
    const filter = LocalStorageUtils.get<object | ''>(this.filterKey);
    const override = this.filterOverride();
    if (filter) {
      this.form().patchValue(filter);
    }
    if (override) {
      this.form().patchValue(override);
    }
    if (filter || override) {
      this.applyFormValueToFilter();
    }
  }

  persistFilter(): void {
    LocalStorageUtils.set(this.filterKey, this.form().value);
  }

  private get filterKey(): string {
    return `${this.localStorageIdentifier()}-filter`;
  }

  private migrateLegacyFilterKey(): void {
    const legacyKey = this.localStorageIdentifier();
    const legacyValue = localStorage.getItem(legacyKey);
    if (legacyValue === null) {
      return;
    }
    if (!LocalStorageUtils.has(this.filterKey)) {
      try {
        LocalStorageUtils.set(this.filterKey, JSON.parse(legacyValue));
      } catch {
        // corrupted legacy value — drop it
      }
    }
    localStorage.removeItem(legacyKey);
  }

  @HostListener('document:keydown.enter', ['$event'])
  handleEnterKey(): void {
    this.applyFormValueToFilter();
  }

  isDeprecated(item: Entity): boolean {
    return (item as { deprecated?: boolean }).deprecated === true;
  }

  onExportClick(event: MouseEvent): void {
    event.stopPropagation();
    const title = `MyGemaPro ${new Date().toDateString()} ${this.headerText() || 'export'}`;

    const dialogRef = this.dialogService.open(ExportFormatDialogComponent, {
      context: {
        header: 'Export Format wählen',
        status: 'primary',
        exportTitle: title,
      },
    });

    dialogRef.onClose.subscribe((result: { format: 'pdf' | 'csv'; count: 'list' | 'all'; title: string }) => {
      if (!result?.format) {
        return;
      }
      if(!result?.count) {
        return;
      }

      if (result.format === 'pdf') {
        this.generatePdf(result.title, result.count);
      } else if (result.format === 'csv') {
        this.generateCsv(result.title, result.count);
      }
    });
  }

  private getNestedValue(item: unknown, key: string, decimals?: number): string {
    const value = key.split('.').reduce<unknown>((obj, k) => (obj as Record<string, unknown> | undefined)?.[k], item);
    // Strings wie '3.7' (Prüfpunktnummer) dürfen nicht als Zahl behandelt werden
    if (typeof value === 'number' && Number.isFinite(value)) {
      // exportDecimals erzwingt feste Stellen für Messgrößen; ohne Angabe werden nur
      // Bruchzahlen gekappt, damit Zähler (Etage, Anzahl) kein '.00' bekommen
      if (decimals != null) {
        return value.toFixed(decimals);
      }
      if (!Number.isInteger(value)) {
        return value.toFixed(2);
      }
    }
    return value != null ? String(value) : '';
  }

  private static readonly EXPORT_BATCH_SIZE = 500;

  private async loadExportItems(count: 'list' | 'all'): Promise<Entity[]> {
    if (count === 'list') {
      const data = await firstValueFrom(this.data$!);
      return data.items;
    }

    const filter = this.filter$.value;
    const batchSize = DataTableComponent.EXPORT_BATCH_SIZE;
    const items: Entity[] = [];

    for (let page = 0; ; page++) {
      const batch = await firstValueFrom(
        this.loadItemsFunction()({ ...filter, page, size: batchSize } as unknown as Filter),
      );
      items.push(...batch);
      if (batch.length < batchSize) {
        break;
      }
    }

    return items;
  }

  private prepareExportData(items: Entity[], title: string) {
    const columns = this.columns().filter((col) => col.exportable);

    const exportData = {
      title,
      headers: columns.map((col) => col.display || col.key),
      rows: items.map((item) =>
        columns.map((col) =>
          col.exportValue
            ? col.exportValue(item)
            : this.getNestedValue(item, col.exportKey ?? col.key, col.exportDecimals),
        ),
      ),
    };

    const actionIndex = exportData.headers.findIndex(
      (h) => h.toLowerCase() === 'aktionen' || h.toLowerCase() === 'actions',
    );

    if (actionIndex !== -1) {
      exportData.headers.splice(actionIndex, 1);
      exportData.rows = exportData.rows.map((row) => {
        const newRow = [...row];
        newRow.splice(actionIndex, 1);
        return newRow;
      });
    }

    return exportData;
  }

  private downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  async generateCsv(title: string, count: 'list' | 'all'): Promise<void> {
    await tryWithLoading(this.exportLoading$, async () => {
      const items = await this.loadExportItems(count);
      const exportData = this.prepareExportData(items, title);

      const csvContent = [
        exportData.headers.join(','),
        ...exportData.rows.map(row =>
          row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      this.downloadFile(blob, `data-table-${exportData.title}.csv`);
    });
  }

  async generatePdf(title: string, count: 'list' | 'all'): Promise<void> {
    await tryWithLoading(this.exportLoading$, async () => {
      try {
        const items = await this.loadExportItems(count);
        const exportData = this.prepareExportData(items, title);

        const blob = await firstValueFrom(
          this.dataTablePdfService.generatePdf(exportData)
        );

        this.downloadFile(blob, `data-table-${exportData.title}.pdf`);
      } catch (error) {
        console.error('Failed to generate or download PDF:', error);
      }
    });
  }
}
